import type { PropsWithChildren, ReactNode } from 'react'
import { useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { twJoin } from 'tailwind-merge'
import { CountBadge } from 'shared/components/count-badge'
import { NotFound } from 'shared/errors/not-found'
import { featureFlagsClient } from 'shared/feature-flags/client'
import { featureFlagKeys } from 'shared/feature-flags/feature-keys'
import useUser, { isDomainOwner, isWsAdmin } from 'shared/hooks/use-user'
import HomeIcon from 'shared/icons/home-icon'
import MembersIcon from 'shared/icons/members-icon'
import ModerationBellIcon from 'shared/icons/moderation-bell-icon'
import ModerationHistoryIcon from 'shared/icons/moderation-history-icon'
import PostsIcon from 'shared/icons/posts-icon'
import { ScheduledPostsIcon } from 'shared/icons/scheduled-posts-icon'
import SettingsIcon from 'shared/icons/settings-icon'
import { useModerationTargetsCount } from 'modules/community/api/get-moderation-target-count'
import { hasPermission } from 'modules/community/utils/abac-roles-permissions'
import { useIsActive } from '../../header/hooks/use-is-active'
import { useCommunityWithTopics } from '../hooks/use-community-with-topics'
import useIsUserMember from '../hooks/use-is-user-member'
import { CommunitySearchForm } from './search-form'
import SidebarJoin from './sidebar-join'
import { Topics } from './topic/topics'

function Sidebar(): ReactNode {
  const router = useRouter()
  const communityPath =
    typeof router.query.communityPath === 'string' ? router.query.communityPath : ''
  const { t } = useTranslation()
  const { isUserMember } = useIsUserMember()
  const { user } = useUser()
  const { data: community, isLoading, error } = useCommunityWithTopics()
  const canViewScheduledPosts = hasPermission(user, 'scheduledPosts', 'schedule', undefined)
  const isReportsEnabled = featureFlagsClient.isOn(featureFlagKeys.enableCommunityReports)
  const canViewModeration = isReportsEnabled && hasPermission(user, 'moderation', 'view', null)
  const { data: alertsCount } = useModerationTargetsCount({
    communityPath: canViewModeration ? communityPath : '',
  })

  useEffect(() => {
    if (error instanceof NotFound) {
      router.replace('/not-found')
    }
  }, [error])

  if (!community || isLoading || !user) {
    return <SidebarLoader />
  }

  const needShowSidebarJoin = !community.private && !isUserMember

  return (
    <aside className="sticky top-26 hidden h-fit w-full max-w-62 flex-col gap-6 lg:flex">
      {needShowSidebarJoin && <SidebarJoin />}
      <nav>
        <ul className="overflow-hidden rounded-lg bg-background-secondary">
          <MainNavItem
            href={`/community/${communityPath}`}
            title={t('sidebar.nav.main.home')}
            key="sidebar-nav-home"
          >
            <HomeIcon />
          </MainNavItem>
          <CommunitySearchForm className="hidden lg:block" />
          <MainNavItem
            href={`/community/${communityPath}/members`}
            title={t('sidebar.nav.main.members')}
            key="sidebar-nav-members"
          >
            <MembersIcon />
          </MainNavItem>
          {canViewModeration && (
            <>
              <MainNavItem
                href={`/community/${communityPath}/moderation/alerts`}
                title={t('community.moderation.alerts.title')}
                count={alertsCount}
                key="sidebar-nav-moderation-alerts"
              >
                <ModerationBellIcon />
              </MainNavItem>
              <MainNavItem
                href={`/community/${communityPath}/moderation/history`}
                title={t('community.moderation.history.title')}
                key="sidebar-nav-moderation-history"
              >
                <ModerationHistoryIcon />
              </MainNavItem>
            </>
          )}
          {(isDomainOwner(user) || isWsAdmin(user)) && (
            <>
              <MainNavItem
                href={`/community/${communityPath}/settings`}
                title={t('sidebar.nav.main.settings')}
                key="sidebar-nav-settings"
              >
                <SettingsIcon />
              </MainNavItem>
              {!community.autoPostsApproval && (
                <MainNavItem
                  href={`/community/${communityPath}/pending-posts`}
                  title={t('sidebar.nav.main.pending_posts')}
                  key="sidebar-nav-pending-posts"
                >
                  <PostsIcon />
                </MainNavItem>
              )}
            </>
          )}
          {canViewScheduledPosts && (
            <MainNavItem
              href={`/community/${communityPath}/scheduled-posts`}
              title={t('sidebar.nav.main.scheduled_posts')}
              key="sidebar-nav-scheduled-posts"
            >
              <ScheduledPostsIcon />
            </MainNavItem>
          )}
          {!community.autoPostsApproval &&
            isUserMember &&
            !isDomainOwner(user) &&
            !isWsAdmin(user) && (
              <MainNavItem
                href={`/community/${communityPath}/pending-posts`}
                title={t('sidebar.nav.main.pending_posts')}
                key="sidebar-nav-members-pending-posts"
              >
                <PostsIcon />
              </MainNavItem>
            )}
        </ul>
      </nav>
      <nav>
        <Topics />
      </nav>
    </aside>
  )
}

export function SidebarLoader(): ReactNode {
  return (
    <aside className="sticky top-6 hidden w-62 lg:block">
      <div className="animate-pulse">
        <div className="mb-12">
          <div className="mb-4 h-6 w-28 rounded-sm bg-skeleton-primary" />
          <div className="h-6 w-28 rounded-sm bg-skeleton-primary" />
        </div>

        <div className="mb-10">
          <div className="mb-6 h-5 w-32 rounded-sm bg-skeleton-primary" />
          <div className="mb-3 h-4 rounded-sm bg-skeleton-primary" />
          <div className="mb-3 h-4 rounded-sm bg-skeleton-primary" />
          <div className="mb-3 h-4 rounded-sm bg-skeleton-primary" />
        </div>

        <div>
          <div className="mb-6 h-5 w-32 rounded-sm bg-skeleton-primary" />
          <div className="mb-3 h-4 rounded-sm bg-skeleton-primary" />
          <div className="mb-3 h-4 rounded-sm bg-skeleton-primary" />
          <div className="mb-3 h-4 rounded-sm bg-skeleton-primary" />
        </div>
      </div>
    </aside>
  )
}

type MainNavItemProps = { href: string; title: string; count?: number }

function MainNavItem({
  href,
  children,
  title,
  count,
}: PropsWithChildren<MainNavItemProps>): ReactNode {
  const isActive = useIsActive(href)
  return (
    <li className="group text-text-primary hover:text-brand-primary">
      <Link
        className={twJoin(
          'flex items-center gap-4 border-l-2 px-2 py-3.5 hover:border-l-brand-primary hover:bg-brand-primary-subtle',
          isActive
            ? 'border-l-brand-primary bg-brand-primary-subtle text-brand-primary'
            : 'border-l-transparent',
        )}
        href={href}
        shallow
      >
        <div
          className={twJoin(
            'flex w-6 shrink-0 items-center justify-center group-hover:text-brand-primary',
            isActive ? 'text-brand-primary' : 'text-text-primary/30',
          )}
        >
          {children}
        </div>
        <span className="flex min-w-0 flex-1 items-center gap-1">
          <span className="min-w-0 flex-1 truncate">{title}</span>
          {typeof count === 'number' && count > 0 && (
            <CountBadge count={count} className="shrink-0" />
          )}
        </span>
      </Link>
    </li>
  )
}

export default Sidebar
